// deps
import React, { memo, useCallback, useMemo } from "react";
import classNames from "classnames";

//internal-deps
import { format, useEffectOnce } from "@swiggy-private/core-ui";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { ISuperButtonProps } from "@swiggy-private/dls-web-core/dist/components/SuperButton/types";

// test-ids
import { SWIGGY_INSTRUMENTS_PAYMENT_METHOD_TEST_IDS } from "@swiggy-private/payment-test-ids";

// constants
import { TEXTS } from "@payment-ui/constants/index";

//styles
import styles from "../SwiggyInstruments.scss";
interface Props {
    isBalanceSufficient: boolean;
    canShowAddMoneyCta: boolean;
    onPayClick: VoidFunction;
    onAddMoneyClick: VoidFunction;
    onAddMoneyView: VoidFunction;
    addMoneyCtaMessage?: string;
    paymentAmount: string;
    paymentMethodDisplayName: string;
}

function ActionButton(props: Props): React.ReactElement | null {
    const { canShowAddMoneyCta, isBalanceSufficient, onPayClick, onAddMoneyClick, onAddMoneyView } = props;
    const ctaClass = classNames({
        [styles.swiggyInsPayButtonContainer]: props.isBalanceSufficient,
        [styles.swiggyInsAddMoneyButtonContainer]: props.canShowAddMoneyCta,
    });

    useEffectOnce(() => {
        if (canShowAddMoneyCta) {
            onAddMoneyView();
        }
    });

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();

            if (isBalanceSufficient) {
                onPayClick();
            } else {
                onAddMoneyClick();
            }
        },
        [isBalanceSufficient, onPayClick, onAddMoneyClick],
    );

    const buttonProps: ISuperButtonProps = useMemo(() => {
        return {
            buttonType: "specialPositive",
            buttonSize: "medium",
            variant: "fullwidth",
            onClick: handleClick,
            "data-testid": isBalanceSufficient
                ? SWIGGY_INSTRUMENTS_PAYMENT_METHOD_TEST_IDS.PAY_BUTTON
                : SWIGGY_INSTRUMENTS_PAYMENT_METHOD_TEST_IDS.ADD_MONEY_CTA,
            className: isBalanceSufficient ? styles.swiggyInsPayButton : styles.swiggyInsAddMoneyButton,
        };
    }, [isBalanceSufficient, onPayClick, onAddMoneyClick]);

    const ctaMessage = useMemo(
        () =>
            isBalanceSufficient
                ? format(TEXTS.PAY_X_WITH_Y, props.paymentAmount, props.paymentMethodDisplayName)
                : props.addMoneyCtaMessage,
        [props.addMoneyCtaMessage, isBalanceSufficient, props.paymentAmount, props.paymentMethodDisplayName],
    );

    if (!isBalanceSufficient && !canShowAddMoneyCta) {
        return null;
    }

    return (
        <div className={ctaClass}>
            <SuperButton {...buttonProps}>{ctaMessage}</SuperButton>
        </div>
    );
}

export default memo(ActionButton);
